import type { SeatmapData, SeatmapField, SeatmapRender, SeatmapSeat, SeatmapSeatType } from '@/types';

// ─── Raw DTO types (as returned by the Perl backend via XMLin) ────────────────
// XMLin serialises all XML attribute values as strings, so numeric fields are
// typed as string here and cast to number in the mapper below.

interface SeatmapSeatTypeDTO {
  /** Zoom multiplier (string from XMLin) */
  z: string;
  /** Name/identifier for this seat type */
  n?: string;
}

interface SeatmapSeatDTO {
  /** X coordinate */
  x: string;
  /** Y coordinate */
  y: string;
  /** Rotation angle in degrees */
  a: string;
  /** Index into t[] (seat type array) */
  t: string;
  /** Group identifier */
  g: string;
  /** Row label */
  f0: string;
  /** Seat label */
  f1: string;
}

interface SeatmapFieldDTO {
  /** Display label */
  d: string;
  /** Distance multiplier (string from XMLin) */
  f: string;
  /** Type name (e.g. "row", "seat") */
  n: string;
}

interface SeatmapRenderDTO {
  /** Hex colour for free seats (without #) */
  f: string;
  /** Hex colour for taken seats (without #) */
  t1: string;
  /** Hex colour for highlighted/our seats (without #) */
  h1: string;
  /** Sort hint */
  sh?: string;
  /** Check-holes setting: '0' disables hole checking */
  ch?: string;
}

export interface SeatmapDataDTO {
  /** Seat type definitions */
  t: SeatmapSeatTypeDTO[];
  /** All seats on this map */
  s: SeatmapSeatDTO[];
  /** Field definitions */
  f: SeatmapFieldDTO[];
  /** Colour configuration */
  r: SeatmapRenderDTO;
  /** Seatmap identifier (appended by the backend) */
  name?: string;
}

// ─── Mapper ───────────────────────────────────────────────────────────────────

function mapSeatType(dto: SeatmapSeatTypeDTO): SeatmapSeatType {
  return {
    zoom: Number(dto.z),
    name: dto.n,
  };
}

function mapSeat(dto: SeatmapSeatDTO, seatTypes: SeatmapSeatType[]): SeatmapSeat {
  const typeIndex = Number(dto.t);
  const zoom = seatTypes[typeIndex]?.zoom ?? 1;
  return {
    x: Number(dto.x),
    y: Number(dto.y),
    angle: Number(dto.a),
    seatTypeIndex: typeIndex,
    group: dto.g,
    row: dto.f0,
    seatNumber: dto.f1,
    highlightRadius: Math.round(20 * zoom * 21.25), // Our old icons were designed for a size 530px at zoom 1, so use that as a baseline for scaling the highlight radius to match the old icons at zoom 1.
  };
}

function mapField(dto: SeatmapFieldDTO): SeatmapField {
  return {
    displayLabel: dto.d,
    distanceMultiplier: Number(dto.f),
    typeName: dto.n,
  };
}

function mapRender(dto: SeatmapRenderDTO): SeatmapRender {
  return {
    colorFree: dto.f,
    colorTaken: dto.t1,
    colorHighlighted: dto.h1,
    sortHint: dto.sh,
    checkHoles: dto.ch,
  };
}

export function mapDtoToSeatmapData(dto: SeatmapDataDTO): SeatmapData {
  const seatTypes = dto.t.map(mapSeatType);
  return {
    seatTypes,
    seats: dto.s.map(s => mapSeat(s, seatTypes)),
    fields: dto.f.map(mapField),
    render: mapRender(dto.r),
    // backgroundUrl is injected by the store after fetching the PNG asset
  };
}
